import React, { lazy } from 'react';
import { List } from 'immutable';
import {
  TM_ORG_ROLES,
  TM_ADMIN_ROLE,
  TM_TEAM_ADMIN_ROLE,
  TM_TEAM_LIMIT_VALUE_FEATURE_DISABLED,
} from '@saucelabs/sl-util-config';

import {
  MAIN_TAB_DEVICES,
  MAIN_TAB_GROUPS,
  MAIN_TAB_INVITATIONS,
  MAIN_TAB_TEAMS,
  MAIN_TAB_USERS,
} from './../../constants';

const UserTableContainer = lazy(() => import('../../users/containers/UserTableContainer'));
const TeamTableContainer = lazy(() => import('../../teams/containers/TeamTableContainer'));
const GroupTableContainer = lazy(() => import('../../groups/containers/GroupTableContainer'));
const InvitationTableContainer = lazy(() => import('../../invitations/containers/InvitationTableContainer'));
const DevicesManagementTableContainer = lazy(() => import('../../devices/containers/DevicesManagementTableContainer'));
const TeamMembersContainer = lazy(() => import('../../team-details/containers/TeamMembersContainer'));
const GroupMembersContainer = lazy(() => import('../../group-details/containers/GroupMembersContainer'));
const GroupTeamsContainer = lazy(() => import('../../group-details/containers/GroupTeamsContainer'));

export function serializeFilters(filters) {
  const roles = filters.get('roles');
  const teams = filters.get('teams');
  const status = filters.get('status');
  const phrase = filters.get('phrase');

  return Object.assign(
    {},
    roles?.size && { roles: roles.join(',') },
    teams?.size && { teams: teams.join(',') },
    status?.size && { status: status.join(',') },
    phrase?.length && { phrase },
  );
}

export function updateFilter(filters, value) {
  if (List.isList(filters)) {
    const index = filters.indexOf(value);
    return index > -1 ? filters.delete(index) : filters.push(value);
  }

  return value;
}

/*
                      team_limit
                     1   +  many
                 +----------------
                  |      |       |      FT - free trial
                 1| FT,S | FT,S  |      S - subscribed
                  |      |  R    |      * - rest (invoiced, admins)
   limit_reached +---------------+
                  |      |       |      Features:
                 0| *    | *     |      - CT - create team
                  | CT,R | CT, R |      - R - admin role, showing teams tab
                 +----------------

  result:
  CT = ~limit_reached
  R = ~limit_reached v team_limit!=1
*/

export const allowToCreateNewTeams = (settings) => (
  !settings.team_limit_reached
);

export const teamFeatureEnabled = (settings) => (
  allowToCreateNewTeams(settings) ||
  settings.team_limit !== TM_TEAM_LIMIT_VALUE_FEATURE_DISABLED
);

export const groupsFeatureEnabled = (settings) => (
  settings.groups_enabled
);

export const devicesFeatureEnabled = (settings) => (
  settings.devices_enabled
);

export const TabsAccessControl = (settings) => ({
  [MAIN_TAB_USERS]: {
    children: () => <UserTableContainer />,
  },
  [MAIN_TAB_TEAMS]: {
    hide: !teamFeatureEnabled(settings),
    children: () => <TeamTableContainer />,
    subTabs: {
      members: {
        children: () => <TeamMembersContainer />,
      },
      devices: {
        hide: !settings.devices_enabled,
        children: () => <DevicesManagementTableContainer readOnly />,
      },
    },
  },
  [MAIN_TAB_GROUPS]: {
    hide: !settings.groups_enabled,
    children: () => <GroupTableContainer />,
    subTabs: {
      teams: {
        children: () => <GroupTeamsContainer />,
      },
      members: {
        children: () => <GroupMembersContainer />,
      },
    },
  },
  [MAIN_TAB_INVITATIONS]: {
    children: () => <InvitationTableContainer />,
  },
  [MAIN_TAB_DEVICES]: {
    children: () => <DevicesManagementTableContainer />,
  },
});

export function filteredOrgRoles({ me }) {
  const allowTeamAdminRole = teamFeatureEnabled(me.organization.settings);
  return TM_ORG_ROLES.filter((role) => {
    // org admin role
    if (role.role === TM_ADMIN_ROLE) {
      return me.isOrgAdmin();
    }

    // team admin role
    if (role.role === TM_TEAM_ADMIN_ROLE) {
      return allowTeamAdminRole;
    }

    return true;
  });
}

export default {
  serializeFilters,
  updateFilter,
};
